import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { switchMap, tap, map } from 'rxjs/operators';
import { SortColumn, SortDirection } from '../_directives/sortable.directive';
import { State } from '../_models/State';
import { Notifications } from '../_models/Notifications';
import { Items } from '../_models/Items';

interface SearchNotificationsResult {
  data: Notifications[];
  total: number;
}

/* 👇 Enhanced compare: handles strings, numbers, and Dates */
const compare = (v1: any, v2: any): number => {
  // Null/undefined handling
  const s1 = v1 ?? '';
  const s2 = v2 ?? '';

  // Date handling
  if (s1 instanceof Date && s2 instanceof Date) {
    return s1.getTime() - s2.getTime();
  }

  // Numeric handling (only if both are actually numbers)
  const n1 = Number(s1);
  const n2 = Number(s2);
  if (!isNaN(n1) && !isNaN(n2) && s1 !== '' && s2 !== '') {
    return n1 - n2;
  }

  // String comparison
  const str1 = String(s1);
  const str2 = String(s2);
  return str1 < str2 ? -1 : str1 > str2 ? 1 : 0;
};

function sort(data: Notifications[], column: SortColumn, direction: string): Notifications[] {
  if (direction === '' || column === '') {
    return data;
  } else {
    return [...data].sort((a, b) => {
      const res = compare(a[column], b[column]);
      return direction === 'asc' ? res : -res;
    });
  }
}

/* 👇 Safer matches: doesn't crash if title is null */
function matches(data: Notifications, term: string) {
  if (!term) return true;
  const t = term.toLowerCase();
  return (
    (data.title && data.title.toLowerCase().includes(t)) ||
    (data.description && data.description.toLowerCase().includes(t))
  );
}

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {

  constructor(private http: HttpClient) {
  }

  private _loading$ = new BehaviorSubject<boolean>(true);
  private _search$ = new Subject<void>();
  private _allData$ = new BehaviorSubject<Notifications[]>([]);
  private _data$ = new BehaviorSubject<Notifications[]>([]);
  private _total$ = new BehaviorSubject<number>(0);
  public notificationss: Notifications[];

  private _state: State = {
    page: 1,
    pageSize: 10,
    searchTerm: '',
    sortColumn: 'startDate',       // 👈 default sort by start date
    sortDirection: 'desc'          // 👈 latest first
  };

  get total$() { return this._total$.asObservable(); }
  get total() { return this._total$.value; }        // 👈 plain number for pagination helpers
  get loading$() { return this._loading$.asObservable(); }
  get page() { return this._state.page; }
  get pageSize() { return this._state.pageSize; }
  get searchTerm() { return this._state.searchTerm; }

  set page(page: number) { this._set({ page }); }
  set pageSize(pageSize: number) { this._set({ pageSize }); }

  set searchTerm(searchTerm: any) {
    this._state.page = 1;                          // 👈 reset page on filter
    this._set({ searchTerm });
  }

  set sortColumn(sortColumn: SortColumn) {
    this._state.page = 1;                          // 👈 reset page on sort
    this._set({ sortColumn });
  }

  set sortDirection(sortDirection: SortDirection) {
    this._state.page = 1;
    this._set({ sortDirection });
  }

  get data$() {
    return this._data$.asObservable();
  }
  get allData$() {
    return this._allData$.asObservable();
  }

  loadItems() {
    return this.http.get<Items[]>(`api/item/all`);
  }

  getById(id) {
    return this.http.get<Notifications[]>(`api/notifications/${id}`);
  }

  getAllData() {
    const url = `api/notifications/all`;
    tap(() => this._loading$.next(true)),
      this.http.get<Notifications[]>(url).subscribe(res => {
        this.notificationss = res || [];

        this._data$.next(this.notificationss);
        this._allData$.next(this.notificationss);

        this._search$.pipe(
          switchMap(() => this._search()),
          tap(() => this._loading$.next(false))
        ).subscribe(result => {
          this._data$.next(result.data);
          this._total$.next(result.total);
        });

        this._search$.next();
      });
  }

  private _set(patch: Partial<State>) {
    Object.assign(this._state, patch);
    this._search$.next();
  }

  private _search(): Observable<SearchNotificationsResult> {
    const { sortColumn, sortDirection, pageSize, page, searchTerm } = this._state;

    // 1. sort
    let sortedData = sort(this.notificationss || [], sortColumn, sortDirection);

    // 2. filter
    sortedData = sortedData.filter(data => matches(data, searchTerm));
    const total = sortedData.length;

    // 3. paginate
    const data = sortedData.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize);
    return of({ data, total });
  }

  clear() {
    this._search$.next();
    this._data$.next(null);
    this._allData$.next(null);
    this._total$.next(null);
    this._loading$.next(null);
    this._state = {
      page: 1,
      pageSize: 10,
      searchTerm: '',
      sortColumn: 'startDate',
      sortDirection: 'desc'
    };
  }

  insert(data) {
    return this.http.post(`api/notifications/insert`, data)
      .pipe(map(res => {
        console.log(res);
        return res;
      }));
  }

  update(updateData) {
    return this.http.post(`api/notifications/update`, updateData)
      .pipe(map(res => {
        console.log(res);
        return res;
      }));
  }

  /* 👇 Delete returns the raw response — caller decides success */
  delete(updateData) {
    return this.http.post(`api/notifications/delete`, updateData);
  }
}